import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentAPI, handleAPIError, patientAPI } from '../services/api';
import Footer from "./landing/Footer"
import AboutHeader from "./AboutHeader"
import { Brain, Calendar, MessageSquare, Shield, Heart, CheckCircle, Clock, Users, Smartphone, BarChart3, Video, User, ArrowRight, Star, FileText } from 'lucide-react';
import Header from './landing/Header';
import SEOHeader from '../components/SEOHeader';


function CarePlan() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [patientProfile, setPatientProfile] = useState(null);

  // Colors
  const marineBlue = '#1e5f74';
  const marineBlueDark = '#164a5a';
  const lightBlue = '#5fb3d4';
  const originalGreen = '#22c55e';
  const originalGreenDark = '#16a34a';

  const plans = [
    {
      id: 'weekly',
      price: '$20',
      period: '/week',
      priceId: 'price_1S2mZ5PNYCu1z4Ed740hZR2s',
      isBestValue: false
    },
    {
      id: 'monthly',
      price: '$79',
      period: '/month',
      priceId: 'price_1S2mZbPNYCu1z4Ed45Nt6bKo',
      isBestValue: true
    }
  ];

  const handlePlanSelection = async (planId) => {
    try {
      setLoading(planId);
      setError('');

      const plan = plans.find(p => p.id === planId);

      // Get logged in user info
      const loggedInUserStr = localStorage.getItem('user');
      const loggedInUser = loggedInUserStr ? JSON.parse(loggedInUserStr) : null;

      // Prepare user info for checkout, prioritizing patient profile data
      const checkoutInfo = {
        priceId: plan.priceId,
        successUrl: `${process.env.FRONTEND_URL || 'https://adhdrapidcare.com'}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/payment/cancel`,
        name: patientProfile?.name || loggedInUser?.name,
        email: patientProfile?.email || loggedInUser?.email,
        phone: patientProfile?.phone || loggedInUser?.phone,
        userId: patientProfile?.user || loggedInUser?._id || loggedInUser?.id,
        countryCode: patientProfile?.countryCode,
        dateOfBirth: patientProfile?.dateOfBirth,
        emergencyContact: patientProfile?.emergencyContact
      };

      console.log('Creating checkout session with info:', {
        ...checkoutInfo,
        email: 'REDACTED',
        phone: checkoutInfo.phone ? 'REDACTED' : undefined,
        userId: checkoutInfo.userId
      });

      const paymentResult = await paymentAPI.createCheckoutSession(checkoutInfo);
      window.location.href = paymentResult.url;
    } catch (err) {
      console.error('Payment initialization error:', err);
      setError(handleAPIError(err, 'Payment initialization failed. Please try again.'));
      setLoading(false);
    }
  };

  // Fetch patient profile on mount only if logged in
  useEffect(() => {
    const fetchPatientProfile = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (token) {
          const response = await patientAPI.getMyProfile();
          setPatientProfile(response.data);
        }
      } catch (err) {
        console.error('Error fetching patient profile:', err);
      }
    };
    fetchPatientProfile();
  }, []);

  // Handle hash-based scrolling
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, []);

  return (
    <>
    <div className="min-h-screen bg-white">
    <SEOHeader
      title="Care Plans | ADHD Rapid Care Australia"
      description="Discover ADHD Rapid Care’s flexible care plans in Australia. Get fast diagnosis, personalised assessments, and ongoing consultations with expert clinicians. Accessible, affordable, and designed to support every step of your ADHD journey."
      path="/care-plan"
    />
    <Header/>

      {/* Hero Section */}
      <section 
        className="text-white py-16" 
        style={{
          backgroundImage: 'linear-gradient(to right, #A5D6A7, #80CBC4)'
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center">
            <Calendar className="h-16 w-16 mb-6 text-green-200" strokeWidth={1.5} />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">ADHD Care Plan</h1>
            <p className="text-xl text-green-100 max-w-4xl mx-auto mb-8">The World’s First Weekly ADHD Monitoring 
Plan</p>
            
            {/* Bulk Billed Card */}
            <div className="w-full max-w-3xl mx-auto mb-8">
              <div 
                className="bg-white rounded-2xl p-8 lg:py-8 shadow-lg"
                style={{
                  backgroundColor: '#FFFFFF',
                  backgroundImage: 'linear-gradient(0deg, rgba(165, 214, 167, 0.12), rgba(165, 214, 167, 0.12))'
                }}
              >
                <h2 className="text-3xl mb-2 text-center" style={{ color: '#333333' }}>Unique SMS check-ins + monthly nurse sessions - AI enhanced</h2>
               <p className="text-center mb-2 mt-8" style={{ color: 'rgb(67, 160, 71)' }}>
                Finally, someone who notices when you're struggling — before you have to ask for help
              </p>

              </div>
            </div>
            
            <div className='w-full max-w-2xl mx-auto'>
                <p className="text-green-100 text-center mb-8  whitespace-nowrap text-xl">Start My Monitoring Plan</p>
            </div>

            {/* Pricing Cards */}
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* Weekly Plan Card */}
                <div className="bg-white bg-opacity-20 rounded-2xl p-6 shadow-lg backdrop-blur-sm">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold mb-2">$20/week</h3>
                    <p className="text-green-100 mb-4">Flexible weekly payment</p>
                    <button 
                      className="w-full bg-white text-green-600 rounded-lg py-2 font-semibold hover:bg-gray-50 transition-colors"
                      onClick={() => handlePlanSelection('weekly')}
                      disabled={loading === 'weekly'}
                    >
                      {loading === 'weekly' ? 'Processing...' : 'Pay Weekly – Start Now'}
                    </button>
                  </div>
                </div>

                  {/* Monthly Plan Card */}
                  <div className="bg-white bg-opacity-20 rounded-2xl p-6 shadow-lg backdrop-blur-sm relative">
                    {/* Best Value Badge */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <div className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                        BEST VALUE
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <h3 className="text-2xl font-bold mb-2">$79/month</h3>
                      <p className="text-green-100 mb-4">Single monthly payment</p>
                      <button 
                        className="w-full bg-white text-green-600 rounded-lg py-2 font-semibold hover:bg-gray-50 transition-colors px-3"
                        onClick={() => handlePlanSelection('monthly')}
                        disabled={loading === 'monthly'}
                      >
                        {loading === 'monthly' ? 'Processing...' : 'Pay Monthly – Start Now'}
                      </button>
                </div>
                  </div>
              </div>


          <div className="mb-14 mt-4">
            <p className="text-center mb-2 mt-8" style={{ color: 'rgb(67, 160, 71)', fontSize: '16px' }}>
              Subscription automatically renews until cancelled – CANCEL ANYTIME
            </p>
          </div>


          </div>


       </div>
      </section>

      {/* Care Plan Features Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What You Get Every Week</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Comprehensive ADHD care designed for your ongoing comfort and success.
            </p>
          </div>

          {/* Feature Icons Row */}
          <div className="grid md:grid-cols-4 gap-8 mb-16">
            {/* Weekly SMS Monitoring */}
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Weekly SMS Monitoring</h3>
              <p className="text-gray-600 text-sm">Regular check-ins via smart SMS</p>
            </div>

            {/* AI Reports */}
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">AI Reports</h3>
              <p className="text-gray-600 text-sm">Intelligent analysis of your progress</p>
            </div>

            {/* Monthly Personal Meeting */}
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Video className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Monthly Personal Meeting</h3>
              <p className="text-gray-600 text-sm">Video or phone session with qualified nurse</p>
            </div>

            {/* ADHD Trained Nurse */}
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-10 h-10 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">ADHD Trained Nurse</h3>
              <p className="text-gray-600 text-sm">Specialist expertise you can trust</p>
            </div>
          </div>

          {/* Additional Benefits and Pricing Card */}
          <div className="">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="grid md:grid-cols-3 ">
                {/* Escalation if needed */}
                <div className="text-center md:border-r md:border-gray-200 md:pr-8 pb-8 md:pb-0">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Escalation if needed</h3>
                  <p className="text-gray-600 text-sm">Direct access to specialists when required</p>
                </div>

                {/* Personalised & private service */}
                <div className="text-center md:border-r md:border-gray-200 md:px-8 pb-8 md:pb-0">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Personalised & private service</h3>
                  <p className="text-gray-600 text-sm">Tailored care that respects your privacy</p>
                </div>

                {/* Pricing */}
                <div className="text-center md:pl-8">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">$79 per month (or $20 per week)</h3>
                  <p className="text-gray-600 text-sm">Subscription - automatically renews until cancelled</p>
                </div>

           


              </div>
            </div>
          </div>
          </div>
      
        <section
          id="pricing"
          className="pt-10 px-4 text-gray-900 " // Changed background
        >
          <div className="max-w-4xl mx-auto text-center">
            {/* Header */}
            <div className="mb-8">
              <button className="text-green-600 text-center mt-8 whitespace-nowrap text-xl" onClick={() => navigate('/login')}>
                Start My Monitoring Plan
              </button>
            </div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-2 gap-8 max-w-xl mx-auto mb-4">
            {/* Weekly Plan Card */}
              <div
                className="rounded-2xl p-6 shadow-lg backdrop-blur-sm relative"
                style={{ backgroundColor: 'rgba(64, 173, 144, 0.8)' }} // 0.4 = 40% opacity
              >
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2 text-white">$20/week</h3>
                <p className="text-green-100 mb-4">Flexible weekly payment</p>
                <button
                  className="w-full bg-white text-green-600 rounded-lg py-2 font-semibold hover:bg-gray-50 transition-colors"
                  onClick={() => handlePlanSelection('weekly')}
                  disabled={loading === 'weekly'}
                >
                  {loading === 'weekly' ? 'Processing...' : 'Pay Weekly – Start Now'}
                </button>
              </div>
              </div>

              {/* Monthly Plan Card */}
              <div
                  className="rounded-2xl p-6 shadow-lg backdrop-blur-sm relative"
                  style={{ backgroundColor: 'rgba(64, 173, 144, 0.8)' }} // 0.4 = 40% opacity
                >
                {/* Best Value Badge */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                    BEST VALUE
                  </div>
                </div>

                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-2 text-white">$79/month</h3>
                  <p className="text-green-100 mb-4">Single monthly payment</p>
                  <button
                    className="w-full bg-white text-green-600 rounded-lg py-2 font-semibold hover:bg-gray-50 transition-colors"
                    onClick={() => handlePlanSelection('monthly')}
                    disabled={loading === 'monthly'}
                  >
                    {loading === 'monthly' ? 'Processing...' : 'Pay Monthly – Start Now'}
                  </button>
                </div>
              </div>
            </div>


          <div className="mb-14">
            <p className="text-center mb-2 mt-8" style={{ color: 'rgb(67, 160, 71)', fontSize: '16px'  }}>
              Subscription automatically renews until cancelled – CANCEL ANYTIME
            </p>
          </div>
          </div>
        </section>
      </section>
      

      {/* Smart SMS Monitoring Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          {/* <div className="text-center mb-6">
            <div className="flex justify-center ">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Smartphone className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </div> */}
          
          <div className="grid md:grid-cols-2 gap-8 items-center mb-5">
            {/* Left side - Image */}
            <div className="flex justify-center">
              <img 
                src="/images/SmartCheck1.png" 
                alt="SmartCheck Monitoring" 
                className="w-full max-w-xl md:max-w-[520px] lg:max-w-[560px] object-contain
                     md:scale-110 lg:scale-125 transform-gpu"
          style={{ maxHeight: 480 }}
              />
            </div>

            {/* Right side - Text */}
            <div>
               <h2 className="text-4xl font-bold  mb-6"style={{ color: 'rgb(30, 95, 116)' }}>
  SmartCheck™
  <span className="block text-gray-900" >
    Weekly Monitoring via SMS
  </span>
</h2>
              <h3 className="text-2xl mb-6 text-green-700">Regular, structured monitoring every week</h3>
              <p className="text-xl text-gray-600 leading-relaxed">
                Never slip through the cracks again. While other services leave you waiting months between appointments, 
                our revolutionary SMS monitoring keeps you connected <strong>every single week</strong>.
              </p>
            </div>
          </div>

          {/* How it works card */}
          <div className="max-w-4xl mx-auto mt-24">
            <div className="bg-blue-50 rounded-2xl p-10 shadow-lg">
              <h3 className="text-2xl font-bold text-blue-800 mb-8 text-center">How it works:</h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-3 rounded-full mt-1">
                      <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Weekly check-ins</h4>
                      <p className="text-sm text-gray-600">via simple SMS questions</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-3 rounded-full mt-1">
                      <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Personalized insights</h4>
                      <p className="text-sm text-gray-600">delivered to your dedicated nurse</p>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-3 rounded-full mt-1">
                      <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">AI analysis</h4>
                      <p className="text-sm text-gray-600">tracks your patterns</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-3 rounded-full mt-1">
                      <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Proactive support</h4>
                      <p className="text-sm text-gray-600">when you need it most</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

           {/* Additional Content Blocks */}
           <div className="max-w-4xl mx-auto mt-12 space-y-12">
             {/* Light Green Block */}
             <div className="bg-green-50 rounded-2xl p-8 shadow-lg">
               <p className="text-gray-800 text-lg leading-relaxed">
                 The result? Your monthly nurse session becomes a <strong>powerful focused discussion</strong> - not generic questions, but targeted support based on <strong>your real-world data</strong>.
               </p>
               <p className="text-gray-800 text-lg leading-relaxed mt-4">
                 This is healthcare that <strong>actually keeps</strong> up with your life. Finally, someone who notices when you're struggling — before you <strong>have to</strong> ask for help.
               </p>
             </div>

             {/* Light Yellow Block */}
             <div className="bg-yellow-50 rounded-2xl p-8 shadow-lg text-center">
               <p className="text-gray-800 text-lg font-semibold">
                 No other ADHD service offers this level of continuous monitoring.
               </p>
               <p className="text-gray-800 text-lg mt-2">
                 Your care plan. Your progress. Every week.
               </p>
             </div>
           </div>
         </div>
       </section>

       {/* Why Ongoing Monitoring Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ongoing Monitoring is Essential</h2>
            <p className="text-xl text-gray-600 max-w-5xl mx-auto mb-8">
              Once patients are diagnosed and begin treatment, ongoing monitoring is essential for legal, clinical,
              and safety reasons.
            </p>

            {/* Legal Notice */}
            <div className="bg-red-50 border-l-4 border-red-500 p-6 max-w-4xl mx-auto">
              <p className="text-lg font-semibold text-red-800">
                By Law: As long as you are being treated for ADHD you must be monitored.
              </p>
            </div>
          </div>

          {/* Three Cards Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Legal Compliance Card */}
            <div className="bg-blue-50 rounded-lg p-8 text-center">
              <div className="mb-6">
                <Shield className="w-12 h-12 mx-auto text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-blue-800 mb-3">Legal Compliance</h3>
              <p className="text-blue-700">
                Regular monitoring ensures compliance with state-based prescribing regulations
                for Schedule 8 medications, which require documented follow-up.
              </p>
            </div>

            {/* Clinical Assessment Card */}
            <div className="bg-green-50 rounded-lg p-8 text-center">
              <div className="mb-6">
                <BarChart3 className="w-12 h-12 mx-auto text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-green-800 mb-3">Clinical Assessment</h3>
              <p className="text-green-700">
                Ongoing reviews allow for accurate assessment of treatment efficacy and
                timely adjustments to medication or dosage.
              </p>
            </div>

            {/* Safety Monitoring Card */}
            <div className="bg-purple-50 rounded-lg p-8 text-center">
              <div className="mb-6">
                <Heart className="w-12 h-12 mx-auto text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-purple-800 mb-3">Safety Monitoring</h3>
              <p className="text-purple-700">
                Continuous oversight helps detect and manage side effects, misuse, or any
                emerging mental health risks.
              </p>
            </div>
          </div>
        </div>
      </section>
    

      {/* Pre-Consultation Symptom Check-in Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          {/* Icon and Title */}
          <div className="mb-12">
            <div className="mx-auto mb-6 flex items-center justify-center">
              <FileText className="w-16 h-16 text-purple-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Final Check-In Before We Meet</h2>
            <p className="text-gray-600 text-lg mb-16">
              Each month before your consultation, SmartCheck™ will send you a short 
              AI-guided form via SMS. It takes only 5–10 minutes and ensures your 
              nurse has the most up-to-date insights before your session.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 items-center bg-purple-50 rounded-2xl px-10 py-8">
            {/* Left Column - Process List */}
            <div className="text-left">
              <h3 className="text-2xl font-bold text-purple-800 mb-4">Simple & Quick Process</h3>
              <div className="space-y-3 text-purple-700">
                {[
                  'Sent by SMS — no login needed',
                  'Takes just 5–10 minutes',
                  'AI-guided questions for depth and accuracy',
                  'Results available to your nurse beforehand'
                ].map((text, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 mt-1 flex-shrink-0 text-purple-600">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <p className="text-sm text-purple-700">{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Time Box */}
            <div className="bg-white rounded-xl px-10 py-8 shadow-md text-center">
              <Clock className="w-12 h-12 text-purple-600 mx-auto mb-3" />
              <div className="text-3xl font-bold text-purple-800 mb-2">5–10</div>
              <div className="text-purple-600 font-semibold">Minutes</div>
              <p className="text-sm text-gray-600 mt-2">Monthly check-in form</p>
            </div>
          </div>

        </div>
      </section>

      {/* Insurance & Rebates Section */}
      <section id="insurance-rebates" className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <Shield className="w-16 h-16 text-blue-600" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Do I Get a Rebate?</h2>
          </div>

          {/* Two Cards Grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Private Health Insurance Card */}
            <div className="bg-green-50 rounded-2xl p-8 shadow-lg border border-green-200">
              <div className="flex justify-center mb-6">
                <div className=" flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-4 text-center">Private Health Insurance</h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                You can claim the cost of your Care Plan back through your private health
insurance. Check with your provider for specific rebate amounts.
              </p>
            </div>

            {/* Medicare Card */}
            <div className="bg-red-50 rounded-2xl p-8 shadow-lg border border-red-200">
              <div className="flex justify-center mb-6">
                <div className=" flex items-center justify-center">
                  <div className="w-8 h-8 text-red-600 font-bold text-2xl">✕</div>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-red-800 mb-4 text-center">Medicare</h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                This service cannot be claimed through Medicare. However, private insurance may provide significant rebates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Choose Your Plan Section */}
      <section id="pricing" className="py-20 px-4 text-white" 
        style={{
          backgroundImage: 'linear-gradient(to right, #A5D6A7, #80CBC4)'
        }}
      >
        <div className="max-w-6xl mx-auto text-center">
          {/* Header */}
         <div className="">
            <h2 className="text-5xl font-bold mb-4 text-white">Your Plan</h2>
            <p className="text-xl text-white font-semibold">Select the payment option that works best for you</p>
             <p className="text-green-100 text-center mb-8 mt-6 whitespace-nowrap text-xl">Start My Monitoring Plan</p>
          </div>
          

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-xl mx-auto mb-2">
            {/* Weekly Plan Card */}
            <div className="bg-white bg-opacity-20 rounded-2xl p-6 shadow-lg backdrop-blur-sm">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">$20/week</h3>
                <p className="text-green-100 mb-4">Flexible weekly payment</p>
                <button 
                  className="w-full bg-white text-green-600 rounded-lg py-2 font-semibold hover:bg-gray-50 transition-colors"
                  onClick={() => handlePlanSelection('weekly')}
                  disabled={loading === 'weekly'}
                >
                  {loading === 'weekly' ? 'Processing...' : 'Pay Weekly – Start Now'}
                </button>
              </div>
            </div>

            {/* Monthly Plan Card */}
            <div className="bg-white bg-opacity-20 rounded-2xl p-6 shadow-lg backdrop-blur-sm relative">
              {/* Best Value Badge */}
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <div className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                  BEST VALUE
                </div>
              </div>
              
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">$79/month</h3>
                <p className="text-green-100 mb-4">Single monthly payment</p>
                <button 
                  className="w-full bg-white text-green-600 rounded-lg py-2 font-semibold hover:bg-gray-50 transition-colors px-3"
                  onClick={() => handlePlanSelection('monthly')}
                  disabled={loading === 'monthly'}
                >
                  {loading === 'monthly' ? 'Processing...' : 'Pay Monthly – Start Now'}
                </button>
              </div>
            </div>
          </div>

          <div className="mb-14">
 
            <p className="text-center mb-2 mt-8" style={{ color: 'rgb(67, 160, 71)', fontSize: '16px'  }}>
              Subscription automatically renews until cancelled – CANCEL ANYTIME
            </p>
          </div>

          {/* Our Qualified ADHD Nurses */}
          <div className="max-w-xl mx-auto">
            <div className="bg-white bg-opacity-20 rounded-2xl p-6 shadow-lg backdrop-blur-sm">
              <div className="flex justify-center mb-4">
                <Users className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Our Qualified ADHD Nurses</h3>
              <p className="text-green-100 text-base">
                All our nurses are highly qualified professionals with specialized ADHD training. 
                You can trust in their expertise and dedication to your care.
              </p>
            </div>
          </div>
        </div>
      </section>

      

      </div>
      <Footer/>
    </>
  );
}

export default CarePlan;